import type { Account, Asset } from "~/v2/api/rest/account";
import type { Quote } from "~/v2/api/rest/data";
import type { CreateOrderRequest } from "~/v2/api/rest/order";
import type {
  OrderType,
  QuantityType,
  Side,
  TimeInForce,
  TrailType,
} from "~/v2/constants/equityOrder";
import { isGTZero } from "../shared/utils";

export interface CreateOrderObjectParams {
  symbol: string;
  side: Side;
  orderType: OrderType;
  timeInForce: TimeInForce;
  quantity: number;
  quantityType: QuantityType;
  limitPrice?: number;
  stopPrice?: number;
  trailType: TrailType;
  trailValue?: number;
  isExtendedHours: boolean;
}

export const createOrderObject = ({
  symbol,
  side,
  orderType,
  timeInForce,
  quantity,
  quantityType,
  limitPrice,
  stopPrice,
  trailType,
  trailValue,
  isExtendedHours,
}: CreateOrderObjectParams): CreateOrderRequest => {
  const isNotional = quantityType === "dollars" && orderType === "market";

  const orderRequest: CreateOrderRequest = {
    symbol,
    side,
    type: orderType,
    time_in_force: timeInForce,
    extended_hours: isExtendedHours,
  };

  if (isNotional) {
    orderRequest.notional = quantity.toString();
  } else {
    orderRequest.qty = quantity.toString();
  }

  if (orderType === "limit" || orderType === "stop_limit") {
    orderRequest.limit_price = limitPrice?.toString();
  }

  if (orderType === "stop" || orderType === "stop_limit") {
    orderRequest.stop_price = stopPrice?.toString();
  }

  if (orderType === "trailing_stop") {
    orderRequest[trailType] = trailValue?.toString();
  }

  return orderRequest;
};

export interface GetIsSubmitDisabledParams {
  account: Account | undefined;
  asset: Asset | undefined;
  quote: Quote | undefined;
  product: "paper" | "live";
  quantity: number | undefined;
  quantityType: QuantityType;
  orderType: OrderType;
  limitPrice: number | undefined;
  stopPrice: number | undefined;
  trailValue: number | undefined;
}

export const getIsSubmitDisabled = ({
  account,
  asset,
  quote,
  product,
  quantity,
  orderType,
  limitPrice,
  stopPrice,
  trailValue,
}: GetIsSubmitDisabledParams): boolean => {
  if (!account || !asset || !product) {
    return true;
  }
  if (orderType !== "limit" && !quote) {
    return true;
  }
  if (!isGTZero(quantity)) {
    return true;
  }
  if (
    (orderType === "limit" || orderType === "stop_limit") &&
    !isGTZero(limitPrice)
  ) {
    return true;
  }
  if (
    (orderType === "stop" || orderType === "stop_limit") &&
    !isGTZero(stopPrice)
  ) {
    return true;
  }
  if (orderType === "trailing_stop" && !isGTZero(trailValue)) {
    return true;
  }
  return false;
};
